import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  HeartHandshake,
  Phone,
  MessageCircle,
  Globe,
  BookOpen,
  Shield,
  ExternalLink,
} from "lucide-react";
import { TopicFilter } from "@/components/TopicFilter";
import { matchesTopic, topicList } from "@/lib/topics";
import { openExternal } from "@/lib/open-external";

const TOPICS = topicList(["trauma", "boundaries", "selfworth", "nervous", "shadow"]);

/**
 * Turn a listed contact detail ("116 123", "Text HOME to 741741") into a
 * tappable call or text link, so a phone can dial it directly. Entries that
 * only point to a website ("Visit website") return null.
 */
function contactLinkFor(
  phone: string,
): { href: string; label: string; kind: "tel" | "sms" } | null {
  const sms = phone.match(/Text\s+([A-Z0-9]+)\s+to\s+([\d\s]{3,})/i);
  if (sms) {
    return {
      href: `sms:${sms[2].replace(/\s/g, "")}?&body=${encodeURIComponent(sms[1])}`,
      label: `Text ${sms[1]}`,
      kind: "sms",
    };
  }
  const digits = phone.replace(/[^\d+]/g, "");
  if (digits.length >= 3 && /\d/.test(phone) && !/visit|see website/i.test(phone)) {
    return { href: `tel:${digits}`, label: `Call ${phone.split(" or ")[0]}`, kind: "tel" };
  }
  return null;
}

export const Route = createFileRoute("/resources")({
  head: () => ({
    meta: [
      { title: "Resources — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Crisis resources, support lines, and reading materials for trauma recovery.",
      },
      { property: "og:title", content: "Resources — Recovery Trauma™" },
      { property: "og:description", content: "Crisis resources, support lines, and reading materials for trauma recovery." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/resources" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/resources" }],
  }),
  component: ResourcesPage,
});

type Resource = {
  name: string;
  description: string;
  phone: string;
  url: string;
  icon: typeof Phone;
};

const COUNTRIES: Record<string, { label: string; resources: Resource[] }> = {
  US: {
    label: "United States",
    resources: [
      { name: "988 Suicide & Crisis Lifeline", description: "24/7 free and confidential support for people in distress.", phone: "988", url: "https://988lifeline.org", icon: Phone },
      { name: "Crisis Text Line", description: "Text-based crisis counseling, 24/7.", phone: "Text HOME to 741741", url: "https://www.crisistextline.org", icon: MessageCircle },
      { name: "RAINN Sexual Assault Hotline", description: "Support for survivors of sexual assault.", phone: "1-800-656-4673", url: "https://www.rainn.org", icon: Shield },
      { name: "SAMHSA National Helpline", description: "Free, confidential 24/7 treatment referral.", phone: "1-800-662-4357", url: "https://www.samhsa.gov/find-help", icon: Globe },
    ],
  },
  UK: {
    label: "United Kingdom",
    resources: [
      { name: "Samaritans", description: "24/7 listening support for anyone in distress.", phone: "116 123", url: "https://www.samaritans.org", icon: Phone },
      { name: "Shout", description: "Free 24/7 text support for anyone in crisis.", phone: "Text SHOUT to 85258", url: "https://giveusashout.org", icon: MessageCircle },
      { name: "Rape Crisis England & Wales", description: "Specialist support for survivors of sexual violence.", phone: "0808 500 2222", url: "https://rapecrisis.org.uk", icon: Shield },
      { name: "Mind Infoline", description: "Mental health information and support.", phone: "0300 123 3393", url: "https://www.mind.org.uk", icon: Globe },
    ],
  },
  CA: {
    label: "Canada",
    resources: [
      { name: "9-8-8 Suicide Crisis Helpline", description: "Free 24/7 call or text support across Canada.", phone: "988", url: "https://988.ca", icon: Phone },
      { name: "Kids Help Phone", description: "24/7 support for young people. Text or call.", phone: "Text CONNECT to 686868", url: "https://kidshelpphone.ca", icon: MessageCircle },
      { name: "Ending Violence Association of Canada", description: "Find a local sexual assault support centre.", phone: "See website", url: "https://endingviolencecanada.org", icon: Shield },
    ],
  },
  AU: {
    label: "Australia",
    resources: [
      { name: "Lifeline", description: "24/7 crisis support and suicide prevention.", phone: "13 11 14", url: "https://www.lifeline.org.au", icon: Phone },
      { name: "Beyond Blue", description: "Mental health support, 24/7.", phone: "1300 22 4636", url: "https://www.beyondblue.org.au", icon: Globe },
      { name: "1800RESPECT", description: "National sexual assault and domestic violence support.", phone: "1800 737 732", url: "https://www.1800respect.org.au", icon: Shield },
    ],
  },
  IE: {
    label: "Ireland",
    resources: [
      { name: "Samaritans Ireland", description: "24/7 listening support.", phone: "116 123", url: "https://www.samaritans.org/ireland", icon: Phone },
      { name: "Pieta House", description: "Free crisis counselling for suicide and self-harm.", phone: "1800 247 247", url: "https://www.pieta.ie", icon: Globe },
      { name: "Dublin Rape Crisis Centre", description: "24/7 national helpline for survivors.", phone: "1800 778 888", url: "https://www.drcc.ie", icon: Shield },
    ],
  },
  DE: {
    label: "Germany",
    resources: [
      { name: "Telefonseelsorge", description: "24/7 free emotional support.", phone: "0800 111 0 111", url: "https://www.telefonseelsorge.de", icon: Phone },
      { name: "Hilfetelefon Gewalt gegen Frauen", description: "24/7 support for women experiencing violence.", phone: "08000 116 016", url: "https://www.hilfetelefon.de", icon: Shield },
    ],
  },
  FR: {
    label: "France",
    resources: [
      { name: "3114 — Numéro national de prévention du suicide", description: "Free 24/7 suicide prevention line.", phone: "3114", url: "https://3114.fr", icon: Phone },
      { name: "SOS Amitié", description: "24/7 emotional listening support.", phone: "09 72 39 40 50", url: "https://www.sos-amitie.com", icon: Globe },
      { name: "Viols Femmes Informations", description: "Support for survivors of sexual violence.", phone: "0 800 05 95 95", url: "https://cfcv.asso.fr", icon: Shield },
    ],
  },
  NL: {
    label: "Netherlands",
    resources: [
      { name: "113 Zelfmoordpreventie", description: "24/7 suicide prevention support.", phone: "113 or 0800-0113", url: "https://www.113.nl", icon: Phone },
      { name: "Centrum Seksueel Geweld", description: "Support after sexual violence.", phone: "0800-0188", url: "https://centrumseksueelgeweld.nl", icon: Shield },
    ],
  },
  INT: {
    label: "International / Other",
    resources: [
      { name: "Find a Helpline", description: "Free, confidential support lines in 130+ countries.", phone: "Visit website", url: "https://findahelpline.com", icon: Globe },
      { name: "IASP Crisis Centres", description: "International directory of crisis centres.", phone: "Visit website", url: "https://www.iasp.info/crisis-centres-helplines/", icon: Phone },
      { name: "Befrienders Worldwide", description: "Emotional support helplines around the world.", phone: "Visit website", url: "https://www.befrienders.org", icon: HeartHandshake },
    ],
  },
};

const ourWorkbooks = [
  { title: "Nervous System Basics", description: "Gentle exercises to map your window of tolerance and notice early signs of dysregulation.", href: "/learn/window-of-tolerance" },
  { title: "Grounding & Resourcing Toolkit", description: "Step-by-step practices for safety, orientation, and coming back to the body.", href: "/grounding" },
  { title: "Boundaries & Reclaiming Voice", description: "Reflection prompts and scripts for practicing 'no' and naming your needs.", href: "/library" },
  { title: "Self-Compassion Practice Pages", description: "Daily check-ins and journaling prompts to soften the inner critic.", href: "/journal" },
];

const recommendedBooks = [
  { title: "The Body Keeps the Score", author: "Bessel van der Kolk", description: "A seminal work on how trauma reshapes the body and brain, and paths to healing." },
  { title: "Complex PTSD: From Surviving to Thriving", author: "Pete Walker", description: "A practical guide for recovering from childhood trauma and emotional neglect." },
  { title: "Trauma and Recovery", author: "Judith Herman", description: "The foundational text that established the stages of trauma recovery." },
];

const selfCareTips = [
  "Prioritize sleep — rest is healing",
  "Move your body gently: walking, stretching, yoga",
  "Eat regular, nourishing meals",
  "Connect with a trusted friend or therapist",
  "Limit news and social media if it feels overwhelming",
  "Practice saying no and setting boundaries",
  "Remember: healing is not linear",
];

const STORAGE_KEY = "resources:country";

function ResourcesPage() {
  const [country, setCountry] = useState<string>("US");
  const [topic, setTopic] = useState<string | null>(null);
  const visibleWorkbooks = ourWorkbooks.filter((wb) => matchesTopic(wb, topic));
  const visibleBooks = recommendedBooks.filter((b) => matchesTopic(b, topic));

  useEffect(() => {
    const saved = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
    if (saved && COUNTRIES[saved]) {
      setCountry(saved);
      return;
    }
    try {
      const locale =
        Intl.DateTimeFormat().resolvedOptions().locale || navigator.language || "";
      const region = locale.split("-")[1]?.toUpperCase();
      if (region && COUNTRIES[region]) setCountry(region);
      else setCountry("INT");
    } catch {
      setCountry("INT");
    }
  }, []);

  const onCountryChange = (value: string) => {
    setCountry(value);
    if (typeof window !== "undefined") localStorage.setItem(STORAGE_KEY, value);
  };

  const current = COUNTRIES[country] ?? COUNTRIES.INT;

  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <div className="mb-8">
        <h1 className="font-serif text-3xl text-foreground">Resources</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Support lines, reading materials, and gentle reminders.
        </p>
      </div>

      <TopicFilter heading="Browse Workbooks & Books" topics={TOPICS} value={topic} onChange={setTopic} />

      <div className="mb-10 rounded-2xl border border-lavender/30 bg-lavender/10 p-6">
        <div className="flex items-center gap-2 text-lavender-deep">
          <HeartHandshake className="h-5 w-5" />
          <h2 className="font-serif text-lg">If you are in crisis</h2>
        </div>
        <p className="mt-2 text-sm text-foreground">
          You are not alone. Help is available right now, 24 hours a day, free and confidential.
        </p>

        <div className="mt-4">
          <label
            htmlFor="country"
            className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted-foreground"
          >
            Your country
          </label>
          <select
            id="country"
            value={country}
            onChange={(e) => onCountryChange(e.target.value)}
            className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {Object.entries(COUNTRIES).map(([code, { label }]) => (
              <option key={code} value={code}>
                {label}
              </option>
            ))}
          </select>
          <p className="mt-1.5 text-xs text-muted-foreground">
            Don't see your country? Choose International for a global directory.
          </p>
        </div>
      </div>

      <section className="mb-12">
        <h2 className="mb-4 font-serif text-xl">
          Crisis support · {current.label}
        </h2>
        <div className="space-y-3">
          {current.resources.map((res) => {
            const Icon = res.icon;
            const action = contactLinkFor(res.phone);
            return (
              <div
                key={res.name}
                className="flex items-start gap-4 rounded-xl border border-border/40 bg-card p-4"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blush/40">
                  <Icon className="h-4 w-4 text-foreground" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-foreground">{res.name}</h3>
                  <p className="mt-0.5 text-sm text-muted-foreground">{res.description}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {action ? (
                      <a
                        href={action.href}
                        className="inline-flex items-center gap-1.5 rounded-full border border-lavender/40 bg-lavender/10 px-3 py-1.5 text-sm font-medium text-foreground"
                      >
                        {action.kind === "sms" ? (
                          <MessageCircle className="h-3.5 w-3.5" />
                        ) : (
                          <Phone className="h-3.5 w-3.5" />
                        )}
                        {action.label}
                      </a>
                    ) : (
                      <span className="text-sm font-medium text-primary">{res.phone}</span>
                    )}
                    <a
                      href={res.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => openExternal(res.url, e)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-border/50 px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
                    >
                      Visit website
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="mb-12">
        <h2 className="mb-4 font-serif text-xl">Gentle self-care reminders</h2>
        <div className="rounded-2xl border border-border/40 bg-card p-5">
          <ul className="space-y-3">
            {selfCareTips.map((tip, i) => (
              <li key={i} className="flex items-start gap-3">
                <LeafIcon />
                <span className="text-sm text-foreground">{tip}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="mb-1 font-serif text-xl">Our workbooks</h2>
        <p className="mb-4 text-sm text-muted-foreground">
          Trauma recovery workbooks created for Recovery Trauma™ members.
        </p>
        <div className="space-y-3">
          {visibleWorkbooks.length === 0 && (
            <p className="rounded-xl border border-border/40 bg-card/60 p-4 text-center text-xs text-muted-foreground">No workbooks in this topic yet.</p>
          )}
          {visibleWorkbooks.map((wb) => (
            <a
              key={wb.title}
              href={wb.href}
              className="block rounded-xl border border-lavender/30 bg-lavender/5 p-5 transition-all hover:border-lavender/50 hover:bg-lavender/10"
            >
              <div className="flex items-start gap-3">
                <BookOpen className="mt-0.5 h-4 w-4 shrink-0 text-lavender-deep" />
                <div className="flex-1">
                  <div className="flex items-center gap-1">
                    <h3 className="text-sm font-medium text-foreground">{wb.title}</h3>
                  </div>
                  <p className="mt-1.5 text-sm text-muted-foreground">{wb.description}</p>
                  <p className="mt-2 text-xs font-medium uppercase tracking-wider text-lavender-deep">
                    Open workbook →
                  </p>
                </div>
              </div>
            </a>
          ))}
        </div>
      </section>

      <section>
        <h2 className="mb-1 font-serif text-xl">Recommended reading</h2>
        <p className="mb-4 text-sm text-muted-foreground">
          Books we love from the wider trauma recovery community.
        </p>
        <div className="space-y-3">
          {visibleBooks.length === 0 && (
            <p className="rounded-xl border border-border/40 bg-card/60 p-4 text-center text-xs text-muted-foreground">No books in this topic yet.</p>
          )}
          {visibleBooks.map((book) => (
            <div key={book.title} className="rounded-xl border border-border/40 bg-card p-5">
              <div className="flex items-start gap-3">
                <BookOpen className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div>
                  <h3 className="text-sm font-medium text-foreground">{book.title}</h3>
                  <p className="text-xs text-muted-foreground">by {book.author}</p>
                  <p className="mt-1.5 text-sm text-muted-foreground">{book.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function LeafIcon() {
  return (
    <svg
      className="mt-1.5 h-3.5 w-3.5 shrink-0 text-lavender-deep"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M11 20A7 7 0 0 1 9.8 6.6C8.3 5.1 6.6 4 4.8 3.5A1 1 0 0 0 3.7 5.1C3.3 8.3 4.3 11.5 6.5 14c.8 1 1.8 1.8 2.8 2.4" />
      <path d="M15 12a7 7 0 0 1 1.2 7.4c-1.5 2.1-4 3.3-6.8 3.3" />
      <path d="M15.5 12.5a7 7 0 0 0-1.2-7.4c-1.5-2.1-4-3.3-6.8-3.3" />
    </svg>
  );
}
